import { FeatureFlagIds } from "../../../utils/featureFlagIds";

import React, { useEffect, useState } from 'react';
import styles from './_finance-quoter.module.scss';
import {
  TabStrip,
  TabStripSelectEventArguments,
  TabStripTab,
} from "@progress/kendo-react-layout";
import FinanceQuoteEditor from './FinanceQuoteEditor/FinanceQuoteEditor';
import FinanceTaxesEditor from './FinanceTaxesEditor/FinanceTaxesEditor';
import FinanceTradesEditor from './FinanceTradesEditor/FinanceTradesEditor';
import {Card, CardContent, IconButton} from "@mui/material";
import LockNotice from "../../Shared/LockNotice";
import AssignmentIcon from "@mui/icons-material/Assignment";
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CardHeader from "@mui/material/CardHeader";
import Divider from "@mui/material/Divider";
import FinanceQuoterSolveButton from "./FinanceQuoterSolveButton/FinanceQuoterSolveButton";
import { Tooltip } from "@progress/kendo-react-tooltip";
import {authService} from "../../../index";
import {httpPostAsync} from "../../../utils/HttpClient";
import {saveFile} from "../../../utils/utils";
import {IConfiguration} from "../../../Data/ConfigurationData";
import {ILookupItems} from "../../../Interfaces/ILookupItems";
import {TabName} from "../../Views/OrderEditor/OrderEditorTabs";
import { IApplication } from "../../../Interfaces/Application/IApplication";
import { ILoggedInEmployee } from "../../../Interfaces/Admin/ILoggedInEmployee";
import {
  ApplicationDownPaymentResponse,
  FinanceLoanClass,
  FinanceQuote,
  FinanceQuoteFee,
  FinanceQuoteTax,
  FinanceWarranty,
  hasValue,
  PermissionSet
} from "@amsanalytics/react-shared-library";
import {
  getQuoteBackgroundColor,
  getSelectedFinanceQuote,
  getZipCodeForPrimary
} from "../../../utils/quoteUtils";
import {IState} from "../../../Interfaces/Common/IState";
import {
  getFinanceQuoteValues,  FinanceQuoteValues
} from "../../../utils/LoanCalcUtils";
import ErrorDialog from "../../Dialogs/ErrorDialog";
import {userHasPermission} from "../../../utils/utils";
import {permissions} from "../../../utils/constants";
import { usePostedAppLock } from "../../../hooks/usePostedAppLock";
import {StatusWorkflowComponent} from "../../Views/OrderEditor/StatusWorkflowComponent";
import FinanceAddonsEditor from "./FinanceAddonsEditor/FinanceAddonsEditor";
import FinanceFeesEditor from "./FinanceFeesEditor/FinanceFeesEditor";
import WashoutReportViewer from "./WashoutReportViewer/WashoutReportViewer";
import { IRuleViolation } from "../../../Interfaces/Application/IApplicationQuote";
import {IEmployee} from "../../../Interfaces/Admin/IEmployee";
import {IQuotePayType} from "../../../Interfaces/Common/IQuotePayType";

/**
 * Base props that come from parent components (not from Redux)
 */
export interface FinanceQuoterBaseProps {
  financeLoanClasses: FinanceLoanClass[];
  configuration: IConfiguration;
  lookups: ILookupItems;
  preventDesking: boolean;
  navigate: (tabName: TabName) => void;
}

/**
 * Props for the library-ready FinanceQuoter component.
 * All state is passed in as props - no Redux or business rules dependencies.
 */
export interface FinanceQuoterProps extends FinanceQuoterBaseProps {
  // Application state (from Redux via container)
  currentApp: IApplication;
  financeQuoteIsDirty: boolean;
  activeUser: ILoggedInEmployee;
  states: IState[];
  applicationFinanceErrors: IRuleViolation[];
  financeQuoteCalcErrors: string[];
  
  // Additional state for child components
  employees: IEmployee[];
  quotePayTypes: IQuotePayType[];
  applicationDownPaymentResponses: ApplicationDownPaymentResponse[];
  
  // Callbacks for state updates (container handles dispatch + business rules)
  onUpdateApp: (updatedApp: IApplication) => void;
  onUpdateSingleQuoteField: <K extends keyof FinanceQuote>(fieldName: K, value: FinanceQuote[K], financeQuoteId?: string) => void;
  onUpdateSelectedQuote: (selectedFinanceQuoteId: string) => void;
  onUpdateTax: (updatedTax: FinanceQuoteTax) => void;
  onUpdateFee: (updatedFee: FinanceQuoteFee) => void;
  onClearCalcErrors: () => void;
  onInitializeDefaultQuotes: () => Promise<void>;
  onZipCodeChange: () => Promise<void>;
  onDocStampFeeUpdate: () => void;
  onLoanClassChange: (loanClass: FinanceLoanClass) => void;
  onSolve: (newVehicle: any) => void;
  onUpdateCustomerAddon: (customerId: string, fieldName: string, value: any) => void;
  onUpdateQuoteAddon: (fieldName: string, value: any) => void;
  
  // Dialog functions (passed from container)
  openDialog: (options: { children: React.ReactNode }) => void;
  closeDialog: () => void;
  
  // Dispatch for components that still need it temporarily (e.g., FinanceTradesEditor for addNewTradeInVehicle)
  dispatch: any;
}

/**
 * Library-ready FinanceQuoter component.
 * All state and callbacks are passed via props from a container component.
 * No Redux, no business rules engine - purely presentational with local UI state only.
 */
export default function FinanceQuoter(props: FinanceQuoterProps) {
  const {
    currentApp,
    financeQuoteIsDirty,
    activeUser,
    states,
    applicationFinanceErrors,
    financeQuoteCalcErrors,
    employees,
    quotePayTypes,
    applicationDownPaymentResponses,
    onUpdateApp,
    onUpdateSingleQuoteField,
    onUpdateSelectedQuote,
    onUpdateTax,
    onUpdateFee,
    onClearCalcErrors,
    onInitializeDefaultQuotes,
    onZipCodeChange,
    onDocStampFeeUpdate,
    onLoanClassChange,
    onSolve,
    openDialog,
    closeDialog,
    dispatch,
  } = props;

  const financeQuotes = currentApp.financeQuotes.filter(afq => !hasValue(afq.deletedDate));
  const selectedFinanceQuote = getSelectedFinanceQuote(currentApp);
  const [selected, setSelected] = useState(0);
  const selectedFinanceLoanClass = props.financeLoanClasses.find(flc => flc.id === selectedFinanceQuote?.financeLoanClassId);
  const disabledFields = props.configuration.financeQuoterDisabledFields;
  const zipCodeForPrimary = getZipCodeForPrimary(currentApp);
  const [financeQuoteValues, setFinanceQuoteValues] = useState<FinanceQuoteValues | undefined>(getFinanceQuoteValues(selectedFinanceQuote, selectedFinanceLoanClass, states));

  const handleUpdateSingleQuoteFieldFromFullQuoter = <K extends keyof FinanceQuote>(
    fieldName: K,
    value: FinanceQuote[K],
    financeQuoteId?: string
  ) => {
    if (selectedFinanceQuote) {
      onUpdateSingleQuoteField(fieldName, value, financeQuoteId ?? selectedFinanceQuote.id);
    }
  };

  const handleUpdateSelectedQuoteFromFullQuoter = (selectedFinanceQuoteId: string) => {
    onUpdateSelectedQuote(selectedFinanceQuoteId);
  }

  const handleUpdateTax = (updatedTax: FinanceQuoteTax): void => {
    onUpdateTax(updatedTax);
    closeDialog();
  }

  const handleUpdateFee = (updatedFee: FinanceQuoteFee): void => {
    onUpdateFee(updatedFee);
    closeDialog();
  }

  const handleWarrantyChange = (warranty: FinanceWarranty | null): void => {
    if (selectedFinanceQuote) {
      handleUpdateSingleQuoteFieldFromFullQuoter("financeWarrantyId", warranty?.id ?? undefined);
    }
  }

  const handleLoanClassChange = (loanClass: FinanceLoanClass): void => {
    onLoanClassChange(loanClass);
  }

  useEffect(() => {
    // If the financeQuote is undefined, initialize it to the version that is in the constants file
    if (financeQuotes.length === 0) {
      onInitializeDefaultQuotes();
    }
  }, [currentApp.financeQuotes.length]);

  useEffect(() => {
    if (selectedFinanceQuote && selectedFinanceLoanClass) {
      onZipCodeChange();
    }
  }, [zipCodeForPrimary]);

  useEffect(() => {
    setFinanceQuoteValues(getFinanceQuoteValues(selectedFinanceQuote, selectedFinanceLoanClass, states));
  }, [selectedFinanceQuote, selectedFinanceLoanClass, states]);



  useEffect(() => {
    if (selectedFinanceLoanClass) {
      onDocStampFeeUpdate();
    }
  },[currentApp.financeQuotes]);

  const quoteBackgroundColor = getQuoteBackgroundColor(financeQuoteIsDirty);

  const viewQuotePresentationReport = async () => {
    const bearerToken = await authService.getToken();
    const report = await httpPostAsync(`api/reporting/getReport`,
      {
        reportName: props.configuration.quotePresentationReport!,
        reportParameters: [{
          name: "AppNumber",
          value: currentApp.applicationNo,
        }],
        reportFormat: "1"
      }, bearerToken!);
    saveFile(report!, 'report.pdf');
  }

  const viewAmortizationScheduleReport = async () => {
    if (!selectedFinanceQuote?.financeQuoteAmortizationSchedulePayments?.length) {
      openDialog({
        children: (
          <ErrorDialog
            title="Amortization Schedule"
            errors={['You must solve the quote to be able to print the amortization schedule.']}
            closeDialog={closeDialog}
          />
        ),
      });
      return;
    }
    const bearerToken = await authService.getToken();
    const report = await httpPostAsync(`api/reporting/getReport`,
      {
        reportName: props.configuration.applicationAmortizationScheduleReport!,
        reportParameters: [{
          name: "ApplicationNumber",
          value: currentApp.applicationNo,
        }],
        reportFormat: "1"
      }, bearerToken!);
    saveFile(report!, 'AmortizationSchedule.pdf');
  }

  const saveApplicationRecapReport = async () => {
    const bearerToken = await authService.getToken();
    const recapReport = await httpPostAsync(`api/reporting/getReport`,
      {
        reportName: 'ApplicationRecap.trdp',
        reportParameters: [{
          name: "ApplicationNumber",
          value: currentApp.applicationNo,
        }],
        reportFormat: "1"
      }, bearerToken!);
    saveFile(recapReport!, 'applicationRecap.pdf');
  }

  const viewWashoutReport = async () => {
    const bearerToken = await authService.getToken();
    const report = await httpPostAsync(`api/reporting/getReport`,
      {
        reportName: props.configuration.washoutReport!,
        reportParameters: [{
          name: "ApplicationNumber",
          value: currentApp.applicationNo,
        }],
        reportFormat: "1"
      }, bearerToken!);
    saveFile(report!, `Washout ${currentApp.applicationNo}.pdf`);
  }

  const { isSectionLocked, lockMessage } = usePostedAppLock();

  const financeQuoteFieldIsDisabled = (fieldName: string): boolean => {
    if (!selectedFinanceQuote) {
      return false;
    }
    if(fieldName === 'tradeInVehicles' && isSectionLocked('Vehicle')) {
      return true;
    }
    // Disable all quoter fields when the Quoter section is locked
    if (isSectionLocked('Quoter')) {
      return true;
    }
    switch (selectedFinanceQuote.solveFor) {
      case 1: // Cash
        if (fieldName === "sellingPrice") {
          return true;
        }
        break;
      case 2: // Payment
        if (fieldName === "payment") {
          return true;
        }
        break;
      case 4: // Term
        if (["numberOfPayments", "termInMonths"].includes(fieldName)) {
          return true;
        }
        break;
      case 5: // Rate
        if (fieldName === "rate") {
          return true;
        }
        break;
      case 999: // DownPayment
        if (fieldName === "downPayment") {
          return true;
        }
        break;
    }

    return disabledFields.includes(fieldName);
  }

  useEffect(() => {
    if (financeQuoteCalcErrors.length > 0) {
      openDialog({
        children: (
          <ErrorDialog
            title="Quote Calculation Errors"
            errors={financeQuoteCalcErrors}
            closeDialog={() => {
              onClearCalcErrors();
              closeDialog();
            }}
          />
        ),
      });
    }
  }, [financeQuoteCalcErrors]);

  const userCanViewProfitCalculator = userHasPermission(activeUser, permissions.viewProfitCalculator, PermissionSet.R);
  const showProfitCalculator = userCanViewProfitCalculator && props.configuration?.featureFlags[FeatureFlagIds.RunProfitCalculatorAfterCarletonQuoteIsRun] === true && !!selectedFinanceQuote;

  const userCanViewWashout = userHasPermission(activeUser, permissions.losWashoutTab, PermissionSet.R);
  const showWashoutTab = userCanViewWashout && !!props.configuration.washoutReport;

  return (
    <div>
      {isSectionLocked('Quoter') && <LockNotice message={lockMessage} />}
      {selectedFinanceQuote && financeQuoteValues &&
        <Card>
          <CardHeader
            avatar={<AssignmentIcon fontSize="small" />}
            title="Quoter"
            action={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {props.configuration.applicationRecapReport &&
                  <Tooltip anchorElement="target" position="top" parentTitle={true}>
                    <IconButton
                      title={'Print Quote'}
                      style={{color: '#757575'}}
                      onClick={() => saveApplicationRecapReport()}
                    >
                      <LocalPrintshopIcon style={{fontSize: '30px'}} />
                    </IconButton>
                  </Tooltip>
                }
                {props.configuration.quotePresentationReport &&
                  <Tooltip anchorElement="target" position="top" parentTitle={true}>
                    <IconButton
                      title={'Print Quote'}
                      style={{color: '#757575'}}
                      onClick={() => viewQuotePresentationReport()}
                    >
                      <LocalPrintshopIcon style={{fontSize: '30px'}} />
                    </IconButton>
                  </Tooltip>
                }
                {props.configuration.applicationAmortizationScheduleReport &&
                  <Tooltip anchorElement="target" position="top" parentTitle={true}>
                    <IconButton
                      title={'Print Amortization Schedule'}
                      style={{color: '#757575'}}
                      onClick={() => viewAmortizationScheduleReport()}
                    >
                      <CalendarMonthIcon style={{fontSize: '30px'}} />
                    </IconButton>
                  </Tooltip>
                }
                {props.configuration.washoutReport && showWashoutTab &&
                  <Tooltip anchorElement="target" position="top" parentTitle={true}>
                    <IconButton
                      title={'Print Washout Report'}
                      style={{color: '#757575'}}
                      onClick={() => viewWashoutReport()}
                    >
                      <AttachMoneyIcon style={{fontSize: '30px'}} />
                    </IconButton>
                  </Tooltip>
                }

                <FinanceQuoterSolveButton currentApp={currentApp} onSolve={onSolve} />
              </div>
            }
          />
          <Divider variant="middle" />
          <CardContent className={styles.financeQuoterContainer} style={{padding: 0, marginTop: 0}}>
            <TabStrip
              selected={selected}
              onSelect={(e: TabStripSelectEventArguments) => setSelected(e.selected)}
              style={{ backgroundColor: quoteBackgroundColor }}
            >
              <TabStripTab title="Quote">
                <FinanceQuoteEditor
                  {...props}
                  selectedFinanceQuote={selectedFinanceQuote}
                  currentApp={currentApp}
                  selectedFinanceLoanClass={selectedFinanceLoanClass}
                  onChangeSingleField={handleUpdateSingleQuoteFieldFromFullQuoter}
                  applicationFinanceErrors={applicationFinanceErrors}
                  financeQuoteFieldIsDisabled={financeQuoteFieldIsDisabled}
                  financeQuoteValues={financeQuoteValues}
                  onChangeSelectedQuote={handleUpdateSelectedQuoteFromFullQuoter}
                  showProfitCalculator={showProfitCalculator}
                  financeQuoteIsDirty={financeQuoteIsDirty}
                  openDialog={openDialog}
                  closeDialog={closeDialog}
                  onUpdateApp={onUpdateApp}
                  onLoanClassChange={handleLoanClassChange}
                  financeWarranties={props.lookups.financeWarranties ?? []}
                  onWarrantyChange={handleWarrantyChange}
                />
              </TabStripTab>
              <TabStripTab title="Fees">
                <FinanceFeesEditor
                  {...props}
                  quoteFees={selectedFinanceQuote.financeQuoteFees}
                  loanClass={selectedFinanceLoanClass}
                  onChange={handleUpdateFee}
                  financeQuoteFieldIsDisabled={financeQuoteFieldIsDisabled}
                  applicationFinanceErrors={applicationFinanceErrors}
                  currentApp={currentApp}
                  openDialog={openDialog}
                  closeDialog={closeDialog}
                />
              </TabStripTab>
              <TabStripTab title="Taxes">
                <FinanceTaxesEditor
                  {...props}
                  applicationQuoteTaxes={selectedFinanceQuote.financeQuoteTaxes}
                  onChange={handleUpdateTax}
                  financeQuoteFieldIsDisabled={financeQuoteFieldIsDisabled}
                  financeQuoteValues={financeQuoteValues}
                  financeLoanClassFinanceTaxes={selectedFinanceLoanClass?.financeLoanClassFinanceTaxes ?? []}
                  openDialog={openDialog}
                  closeDialog={closeDialog}
                />
              </TabStripTab>
              <TabStripTab title="Trades">
                <FinanceTradesEditor
                  currentApp={currentApp}
                  financeQuoteFieldIsDisabled={financeQuoteFieldIsDisabled}
                  openDialog={openDialog}
                  closeDialog={closeDialog}
                  onUpdateApp={onUpdateApp}
                  dispatch={dispatch}
                />
              </TabStripTab>
              <TabStripTab title="Addons">
                <FinanceAddonsEditor
                  financeQuoteFieldIsDisabled={financeQuoteFieldIsDisabled}
                  applicationFinanceErrors={applicationFinanceErrors}
                  selectedFinanceQuote={selectedFinanceQuote}
                  selectedFinanceLoanClass={selectedFinanceLoanClass}
                  lookups={props.lookups}
                  appCustomers={currentApp.appCustomers}
                  onUpdateCustomerAddon={props.onUpdateCustomerAddon}
                  onUpdateQuoteAddon={props.onUpdateQuoteAddon}
                />
              </TabStripTab>
              {showWashoutTab &&
                <TabStripTab title="Washout">
                  {selectedFinanceQuote?.financeQuoteAmortizationSchedulePayments?.length ? (
                    <WashoutReportViewer
                      key={`washout-${selectedFinanceQuote.id}-${selectedFinanceQuote.modifiedDate}`}
                      applicationNo={currentApp.applicationNo}
                      reportName={props.configuration.washoutReport!}
                      clientId={props.configuration.clientId}
                    />
                  ) : (
                    <div className={styles.washoutMessage}>
                      You must solve the quote to see the washout report.
                    </div>
                  )}
                </TabStripTab>
              }
            </TabStrip>
          </CardContent>
        </Card>
      }
      <StatusWorkflowComponent {...props} disableDeskingOrPosting={props.preventDesking} />
    </div>
  );
}
